import { supabase } from './supabase';

// Like/Unlike Posts
export const likePost = async (postId: string, userId: string): Promise<void> => {
  const { error } = await supabase
    .from('post_likes')
    .insert([{ post_id: postId, user_id: userId }]);

  if (error) throw error;
};

export const unlikePost = async (postId: string, userId: string): Promise<void> => {
  const { error } = await supabase
    .from('post_likes')
    .delete()
    .eq('post_id', postId)
    .eq('user_id', userId);

  if (error) throw error;
};

// Comments
export const addComment = async (
  postId: string,
  userId: string,
  content: string
): Promise<void> => {
  const { error } = await supabase
    .from('post_comments')
    .insert([
      {
        post_id: postId,
        user_id: userId,
        content,
      }
    ]);

  if (error) throw error;
};

export const deleteComment = async (commentId: string, userId: string): Promise<void> => {
  const { error } = await supabase
    .from('post_comments')
    .delete()
    .eq('id', commentId)
    .eq('user_id', userId);

  if (error) throw error;
};

export const updateComment = async (
  commentId: string,
  userId: string,
  content: string
): Promise<void> => {
  const { error } = await supabase
    .from('post_comments')
    .update({ content })
    .eq('id', commentId)
    .eq('user_id', userId);

  if (error) throw error;
};

// Like/Unlike Comments
export const likeComment = async (commentId: string, userId: string): Promise<void> => {
  const { error } = await supabase
    .from('comment_likes')
    .insert([{ comment_id: commentId, user_id: userId }]);

  if (error) throw error;
};

export const unlikeComment = async (commentId: string, userId: string): Promise<void> => {
  const { error } = await supabase
    .from('comment_likes')
    .delete()
    .eq('comment_id', commentId)
    .eq('user_id', userId);

  if (error) throw error;
};

// Save/Unsave Posts
export const savePost = async (
  postId: string,
  userId: string,
  collectionName: string = 'All Posts'
): Promise<void> => {
  const { error } = await supabase
    .from('saved_posts')
    .insert([
      {
        post_id: postId,
        user_id: userId,
        collection_name: collectionName,
      }
    ]);

  if (error) throw error;
};

export const unsavePost = async (postId: string, userId: string): Promise<void> => {
  const { error } = await supabase
    .from('saved_posts')
    .delete()
    .eq('post_id', postId)
    .eq('user_id', userId);

  if (error) throw error;
};

export const getSavedPosts = async (userId: string): Promise<any[]> => {
  const { data, error } = await supabase
    .from('saved_posts')
    .select(`
      *,
      posts (
        *,
        profiles:user_id (
          id,
          username,
          full_name,
          avatar_url
        ),
        post_media (
          id,
          media_url,
          media_type,
          order_index
        )
      )
    `)
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
};

export const getUserCollections = async (userId: string): Promise<string[]> => {
  const { data, error } = await supabase
    .from('saved_posts')
    .select('collection_name')
    .eq('user_id', userId);

  if (error) throw error;

  const collections = [...new Set(data?.map(item => item.collection_name) || [])];
  return collections;
};

// Follow/Unfollow - Updated to use new followService
export const followUser = async (followerId: string, followingId: string): Promise<void> => {
  const { error } = await supabase
    .from('follows')
    .insert([
      {
        follower_id: followerId,
        following_id: followingId,
      }
    ]);

  if (error) throw error;
};

export const unfollowUser = async (followerId: string, followingId: string): Promise<void> => {
  const { error } = await supabase
    .from('follows')
    .delete()
    .eq('follower_id', followerId)
    .eq('following_id', followingId);

  if (error) throw error;
};

export const getFollowers = async (userId: string): Promise<any[]> => {
  const { data, error } = await supabase
    .from('follows')
    .select(`
      *,
      profiles:follower_id (
        id,
        username,
        full_name,
        avatar_url,
        bio
      )
    `)
    .eq('following_id', userId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
};

export const getFollowing = async (userId: string): Promise<any[]> => {
  const { data, error } = await supabase
    .from('follows')
    .select(`
      *,
      profiles:following_id (
        id,
        username,
        full_name,
        avatar_url,
        bio
      )
    `)
    .eq('follower_id', userId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
};

export const isFollowing = async (followerId: string, followingId: string): Promise<boolean> => {
  const { data, error } = await supabase
    .from('follows')
    .select('id')
    .eq('follower_id', followerId)
    .eq('following_id', followingId)
    .maybeSingle();

  if (error) throw error;
  return !!data;
};

export const getFollowCounts = async (userId: string): Promise<{ followers: number; following: number }> => {
  const [followersResult, followingResult] = await Promise.all([
    supabase
      .from('follows')
      .select('*', { count: 'exact', head: true })
      .eq('following_id', userId),
    supabase
      .from('follows')
      .select('*', { count: 'exact', head: true })
      .eq('follower_id', userId)
  ]);

  return {
    followers: followersResult.count || 0,
    following: followingResult.count || 0,
  };
};

// Notifications
export const getNotifications = async (userId: string): Promise<any[]> => {
  const { data, error } = await supabase
    .from('notifications')
    .select(`
      *,
      actor:profiles!notifications_actor_id_fkey (
        id,
        username,
        full_name,
        avatar_url
      ),
      post:posts (
        id,
        post_media (
          media_url,
          media_type
        )
      ),
      comment:post_comments (
        id,
        content
      )
    `)
    .eq('recipient_id', userId)
    .order('created_at', { ascending: false })
    .limit(50);

  if (error) throw error;
  return data || [];
};

export const markNotificationAsRead = async (notificationId: string): Promise<void> => {
  const { error } = await supabase
    .from('notifications')
    .update({ is_read: true })
    .eq('id', notificationId);

  if (error) throw error;
};

export const markAllNotificationsAsRead = async (userId: string): Promise<void> => {
  const { error } = await supabase
    .from('notifications')
    .update({ is_read: true })
    .eq('recipient_id', userId)
    .eq('is_read', false);

  if (error) throw error;
};

export const getUnreadNotificationCount = async (userId: string): Promise<number> => {
  const { count, error } = await supabase
    .from('notifications')
    .select('*', { count: 'exact', head: true })
    .eq('recipient_id', userId)
    .eq('is_read', false);

  if (error) throw error;
  return count || 0;
};